<template>
  <b-container class="flex-grow-1 d-flex flex-column">
    <div class="d-flex flex-row align-items-center">
      <h1 class="my-5 mr-4">
        <b>{{ $t('project_dashboard') }}</b>
      </h1>
    </div>

    <b-row>
      <b-col>
        <h2 class="my-5">
          <b>{{ $t('project_status') }}</b>
        </h2>
        <h4>
          <span class="fas fa-check-circle text-success" />
          {{ $t('in_production') }}
        </h4>
      </b-col>

      <b-col>
        <h2 class="my-5">
          <b>{{ $t('uptime') }}</b>
        </h2>
        <h4>
          98 %
        </h4>
      </b-col>

      <b-col>
        <h2 class="my-5">
          <!-- <b>{{ $t('recent_activity') }}</b> -->
          <b>{{ $t('current_activity') }}</b>
        </h2>
        <h4>
          {{ $t('waiting_for_files', { period_id: 202011 }) }}
        </h4>
      </b-col>
    </b-row>
    <!-- <p>
      Computing 202011 predictions
    </p>
    <p>
      Auto-retraining with new 202011 data
    </p> -->

    <h2 class="my-5">
      <b>{{ $t('performance_monitoring') }}</b>
    </h2>
    <b-card class="position-relative">
      <canvas ref="performance-chart" />
    </b-card>

    <h2 class="my-5">
      <b>{{ $t('deliveries') }}</b>
    </h2>
    <div v-for="period_id in period_ids">
      <unroll-element
        :title="period_id"
        :available="has_events(period_id)"
        :start_opened="has_events(period_id)"
      >
        <b-table
          responsive
          striped
          hover
          :fields="$options.event_fields"
          :items="event_items_per_period_id[period_id]"
          class="synaplus-cosmo-scroll-higher-720"
        />
      </unroll-element>
    </div>
  </b-container>
</template>

<i18n>
{
  "en": {
    "project_dashboard": "Project Dashboard",
    "uptime": "Uptime",
    "current_activity": "Current activity",
    "waiting_for_files": "Waiting for {period_id} files",
    "performance_monitoring": "Performance monitoring",
    "performance": "Performance",
    "deliveries": "Deliveries",

    "updated_project_event": "Production project updated: {model_name}",
    "prediction_start_event": "Predicting conversion_probability for {period_id}",
    "prediction_stop_event": "{period_id} conversion_probability available",
    "scoring_start_event": "Scoring {period_id}",
    "scoring_stop_event": "{period_id} score: {value}",
    "performance_drop_event": "{period_id} performance drop: {value} < {threshold}",
    "missing_column_event": "Missing {column_name} column in table {table_name}",
    "empty_column_event": "Empty {column_name} column in table {table_name}",
    "surprise_contents_event": "{column_name} column distribution changed in table {table_name}"
  },
  "fr": {
    "project_dashboard": "Suivi du projet",
    "uptime": "Disponibilité",
    "current_activity": "En cours",
    "waiting_for_files": "En attente des fichiers {period_id}",
    "performance_monitoring": "Suivi des performances",
    "performance": "Performance",
    "deliveries": "Livraisons",

    "updated_project_event": "Projet en production mis à jour : {model_name}",
    "prediction_start_event": "Prédiction de probabilite_resiliation pour {period_id}",
    "prediction_stop_event": "probabilite_resiliation {period_id} disponible",
    "scoring_start_event": "Calcul du score {period_id}",
    "scoring_stop_event": "Score {period_id}: {value}",
    "performance_drop_event": "Baisse de performance en {period_id} : {value} < {threshold}",
    "missing_column_event": "Colonne {column_name} manquante dans la table {table_name}",
    "empty_column_event": "Colonne {column_name} vide dans la table {table_name}",
    "surprise_contents_event": "Changement de distribution pour la colonne {column_name} dans la table {table_name}"
  }
}
</i18n>

<style lang="scss" scoped>
@import '@/custom-bootstrap.scss';
</style>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import Chart from 'chart.js'
import 'chartjs-plugin-annotation'
// import ChartDataLabels from 'chartjs-plugin-datalabels'

import chartColors from '@/components/chart_colors.js'
import UnrollElement from './unroll_element.vue'

// *****************************************************************************

class PerformanceChart {
  constructor(el, title, y_min, y_max) {
    this.el = el
    this.title = title
    this.y_min = y_min
    this.y_max = y_max
  }
  init() {
    this.real_chart = new Chart(this.el, {
      // type: 'scatter',
      type: 'multicolorScatter',
      data: {
        // labels: [0, 1, 2, 3, 4, 5, 6],
        datasets: [],
      },
      options: {
        responsive: true,
        title: {
          display: true,
          text: this.title,
        },
        aspectRatio: 16.0 / 9,
        scales: {
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: 'Month',
              },
            },
          ],
          yAxes: [
            {
              scaleLabel: {
                display: true,
                // labelString: 'Gini',
                labelString: 'Score',
              },
              ticks: { min: 0, max: 1 },
            },
          ],
        },
        legend: { display: false },
        // fillColor: 'rgba(255, 128, 0, 0.8)',
        hover: {
          mode: 'nearest',
          intersect: false,
        },
        // events: [],
        tooltips: {
          filter(tooltipItem) {
            return tooltipItem.datasetIndex === 0
          },
        },
        // plugins: [ChartDataLabels],
        // https://github.com/chartjs/chartjs-plugin-annotation/tree/v0.5.7
        // https://github.com/chartjs/chartjs-plugin-annotation/issues/156#issuecomment-499117786
        annotation: {
          annotations: [
            {
              type: 'box',
              drawTime: 'beforeDatasetsDraw',
              yScaleID: 'y-axis-1',
              yMin: this.y_min,
              yMax: this.y_max,
              backgroundColor: 'rgba(0, 255, 0, 0.3)',
            },
          ],
        },
      },
    })
  }

  update(datasets, labels) {
    this.real_chart.data.datasets = datasets
    this.real_chart.data.labels = labels
    this.real_chart.options.scales.xAxes[0].ticks = {
      min: 0,
      max: labels.length - 1,
      stepSize: 1,
      callback(value, index, values) {
        const res = labels[parseInt(value)]
        return res
      },
    }
    this.real_chart.update()
  }
}

function parse_period_id(period_id) {
  const s = period_id.toString()
  const regex = /^(\d\d\d\d)(\d\d)$/
  const groups = s.match(regex)
  let year, month
  ;[period_id, year, month] = groups
  let res = [year, month]
  res = _.map(res, o => parseInt(o))
  return res
}

function parse_time(time) {
  const s = time.toString()
  const regex = /^(\d\d):(\d\d)$/
  const groups = s.match(regex)
  let hours, minutes
  ;[time, hours, minutes] = groups
  let res = [hours, minutes]
  res = _.map(res, o => parseInt(o))
  return res
}

function generate_performance_data() {
  const res = {
    202003: 0.8,
    202004: 0.77,
    202005: 0.4,
    // 202006: 0.69,
    // 202007: 0.72,
    // 202008: 0.7,
    // 202009: 0.65,
    // 202010: 0.68,
    202006: 0.69,
    202007: 0.72,
    202008: 0.65,
    202009: 0.68,
  }
  return res
}

function generate_event_objects(performance_data) {
  // const filename = 'Contracts_<period_id>'
  const table_name = 'prospects'
  const res = [
    { period_id: 202011 },
    {
      period_id: 202010,
      start: '02 11:27',
      end: '03 08:12',
      events: [
        // [
        //   'missing_column',
        //   '02 11:40',
        //   {
        //     table_name,
        //     column_name: 'resilie',
        //   },
        // ],
        [
          'scoring_stop',
          '03 08:24',
          {
            period_id: '<last_period_id>',
            value: '<last_period_id_score>',
          },
        ],
      ],
    },
    {
      period_id: 202009,
      start: '02 08:14',
      end: '03 07:01',
      events: [
        [
          'empty_column',
          '02 15:34',
          {
            table_name,
            column_name: 'prospect_category',
          },
        ],
        [
          'scoring_stop',
          '03 07:19',
          {
            period_id: '<last_period_id>',
            value: '<last_period_id_score>',
          },
        ],
      ],
    },
    {
      period_id: 202008,
      start: '02 09:12',
      end: '03 12:53',
      events: [
        [
          'missing_column',
          '02 09:40',
          {
            table_name,
            column_name: 'department',
          },
        ],
        [
          'updated_project',
          '02 14:53',
          { model_name: 'Prospection_<period_id>' },
        ],
        ['prediction_start', '02 14:54'],
        [
          'scoring_stop',
          '03 13:11',
          {
            period_id: '<last_period_id>',
            value: '<last_period_id_score>',
          },
        ],
      ],
    },
    {
      period_id: 202007,
      start: '01 23:33',
      end: '02 21:15',
      events: [
        [
          // [
          //   'surprise_contents',
          //   '02 06:12',
          //   {
          //     table_name,
          //     column_name: 'category',
          //   },
          // ],
          // [
          'scoring_stop',
          '02 21:30',
          {
            period_id: '<last_period_id>',
            value: '<last_period_id_score>',
          },
        ],
      ],
    },
    {
      period_id: 202006,
      start: '02 18:15',
      end: '03 16:50',
      events: [
        [
          'scoring_stop',
          '03 20:30',
          {
            period_id: '<last_period_id>',
            value: '<last_period_id_score>',
          },
        ],
        [
          'performance_drop',
          '03 20:30',
          {
            period_id: '<last_period_id>',
            value: '0.4',
            // threshold: this.y_min,
            threshold: '0.6',
          },
        ],
      ],
    },
    {
      period_id: 202005,
      start: '02 16:20',
      end: '03 14:00',
      events: [
        [
          'missing_column',
          '02 16:35',
          {
            table_name,
            column_name: 'department',
          },
        ],
        [
          'scoring_stop',
          '03 14:20',
          {
            period_id: '<last_period_id>',
            value: '<last_period_id_score>',
          },
        ],
      ],
    },
    {
      period_id: 202004,
      start: '02 17:03',
      end: '03 15:40',
      events: [
        // [
        //   'scoring_start',
        //   '03 15:40',
        //   {
        //     period_id: '<last_period_id>',
        //   },
        // ],
        [
          'scoring_stop',
          '03 15:55',
          {
            period_id: '<last_period_id>',
            value: '<last_period_id_score>',
          },
        ],
      ],
    },
    {
      period_id: 202003,
      start: '02 11:20',
      end: '03 12:05',
      events: [
        [
          'surprise_contents',
          '02 11:30',
          {
            table_name,
            column_name: 'prospect_id',
          },
        ],
      ],
    },
    {
      period_id: 202002,
      events: [
        [
          'updated_project',
          '15 10:14',
          { model_name: 'Prospection_<period_id>' },
        ],
      ],
    },
  ]
  for (let event_o of res) {
    let { period_id, end = null, events = null } = event_o
    if (_.isNull(events)) {
      events = []
      event_o.events = events
    }
    if (!_.isNull(end) && period_id != 202003) {
      events.push([
        'scoring_start',
        end,
        {
          period_id: '<last_period_id>',
        },
      ])
    }
    for (const [_name, _day_time, params = null] of events) {
      if (!_.isNull(params)) {
        for (let [key, value] of _.entries(params)) {
          value = value.replace(/<period_id>/, period_id.toString())
          value = value.replace(/<last_period_id>/, (period_id - 1).toString())
          value = value.replace(
            /<last_period_id_score>/,
            (
              performance_data[period_id - 1] || '<last_period_id_score>'
            ).toString()
          )
          console.log(params, key, value, period_id)
          params[key] = value.replace(/<period_id>/, period_id.toString())
        }
      }
    }
  }
  return res
}

export default {
  name: 'ProjectDashboard',
  components: { UnrollElement },
  data() {
    const performance_data = generate_performance_data()
    const event_objects = generate_event_objects(performance_data)
    return {
      performance_data,
      y_min: 0.6,
      y_max: 0.8,
      chart_object: undefined,

      event_objects,
    }
  },

  computed: {
    i18n_locale() {
      return this.$root.$i18n.locale
    },
    completed_performance_data() {
      const res = []
      const thresholds = [this.y_min, this.y_max]
      const period_ids = _.keys(this.performance_data).sort()
      const performance = _.map(_.range(period_ids.length), i => ({
        // x: i ** 2,
        x: i,
        y: this.performance_data[period_ids[i]],
      }))
      const data = [performance[0], ...performance]
      for (const [start, stop] of _.zip(_.dropRight(data), _.drop(data))) {
        for (const threshold of thresholds) {
          if (start.y > threshold != stop.y > threshold) {
            let mid = {
              x:
                start.x +
                ((stop.x - start.x) / (stop.y - start.y)) *
                  (threshold - start.y),
              y: threshold,
            }
            res.push(mid)
          }
        }
        res.push(stop)
      }
      return res
    },
    performance_dataset() {
      const res = _.merge(
        {
          label: 'performance',
          backgroundColor: chartColors.blue,
          // borderColor: chartColors.blue,
          // showLine: false,
        },
        this.$options.performance_dataset_options
      )

      // const add_points = function()

      const period_ids = _.keys(this.performance_data).sort()
      const performance = _.map(_.range(period_ids.length), i => ({
        // x: i ** 2,
        x: i,
        y: this.performance_data[period_ids[i]],
      }))

      const point_colors = _.map(performance, v =>
        v.y >= this.y_min ? 'blue' : 'red'
      )

      // res.colors = ['', 'red', 'green', 'green', 'blue']
      res.colors = ['', 'transparent']
      res.pointBackgroundColor = point_colors
      // res.fill = false

      res.data = performance

      return res
    },
    completed_performance_dataset() {
      const res = _.merge(
        {
          label: 'completed_performance',
          backgroundColor: chartColors.blue,
          // borderColor: chartColors.blue,
          showLine: true,
          pointRadius: 0,
          // tooltips: { enabled: false },
          // hover: { mode: null },
          // events: [],
          hoverRadius: 0,
        },
        this.$options.performance_dataset_options
      )

      // const add_points = function()

      const point_colors = _.map(this.completed_performance_data, v =>
        v.y >= this.y_min ? 'blue' : 'red'
      )

      const colors = ['']
      for (const [start, stop] of _.zip(
        _.dropRight(this.completed_performance_data),
        _.drop(this.completed_performance_data)
      )) {
        let color = 'blue'
        for (const point of [start, stop]) {
          if (point.y < this.y_min) {
            color = 'red'
          }
        }
        colors.push(color)
      }
      res.colors = colors
      // res.colors = ['', 'red', 'green', 'green', 'blue', 'red', 'blue']
      res.pointBackgroundColor = point_colors
      // res.fill = false

      res.data = this.completed_performance_data

      return res
    },
    datasets() {
      // return [this.performance_dataset]
      return [this.performance_dataset, this.completed_performance_dataset]
    },
    event_items_per_period_id() {
      const prepare = function(self, type, period_id, day_time, params = null) {
        const regex = /(\d\d) (\d\d:\d\d)/
        const m = day_time.match(regex)
        let day, time
        ;[day_time, day, time] = m
        const res = self.prepare_event(type, period_id, day, time, params)
        return res
      }

      const event_infos = []
      for (const { period_id, start, end, events } of this.event_objects) {
        let infos = []
        if (!_.isUndefined(start)) {
          infos.push(['prediction_start', start])
        }
        if (!_.isUndefined(end)) {
          infos.push(['prediction_stop', end])
        }
        infos = [...infos, ...(events || [])]
        // infos = _.sortBy(infos, [o => o[1]])
        infos = _.reverse(_.sortBy(infos, [o => o[1]]))
        event_infos.push({ period_id, infos })
      }

      const res = {}
      for (const { period_id, infos } of event_infos) {
        const items = []
        for (const info of infos) {
          const args = [this, info[0], period_id, ..._.slice(info, 1)]
          const item = prepare(...args)
          items.push(item)
        }

        res[period_id] = items
      }
      return res
    },

    period_ids() {
      return _.reverse(_.keys(this.event_items_per_period_id).sort())
    },
  },

  beforeMount() {
    this.$options.event_to_color = {
      updated_project: 'info',
      prediction_start: 'lightblue',
      prediction_stop: 'success',
      scoring_start: 'lightblue',
      scoring_stop: 'success',
      // performance_drop: 'warning',
      performance_drop: 'darkred',
      missing_column: 'warning',
      empty_column: 'warning',
      surprise_contents: 'warning',
    }
    this.$options.performance_dataset_options = {
      cubicInterpolationMode: 'monotone',
      fill: false,
    }
    this.$options.event_fields = [
      {
        key: 'date',
        label: 'Date',
        class: 'text-center',
        variant: 'primary',
      },
      {
        key: 'event',
        label: 'Event',
        class: 'text-center',
        variant: 'primary',
      },
    ]
    // https://jsfiddle.net/egamegadrive16/zjdwr4fh/
    Chart.defaults.multicolorScatter = Chart.defaults.scatter
    Chart.controllers.multicolorScatter = Chart.controllers.scatter.extend({
      draw(ease) {
        var startIndex = 0,
          meta = this.getMeta(),
          points = meta.data || [],
          colors = this.getDataset().colors,
          area = this.chart.chartArea,
          originalDatasets = meta.dataset._children.filter(function(data) {
            return !isNaN(data._view.y)
          })

        function _setColor(newColor, meta) {
          meta.dataset._view.borderColor = newColor
        }

        if (!colors) {
          Chart.controllers.scatter.prototype.draw.call(this, ease)
          return
        }

        for (var i = 2; i <= colors.length; i++) {
          if (colors[i - 1] !== colors[i]) {
            _setColor(colors[i - 1], meta)
            meta.dataset._children = originalDatasets.slice(startIndex, i)
            meta.dataset.draw()
            startIndex = i - 1
          }
        }

        meta.dataset._children = originalDatasets.slice(startIndex)
        meta.dataset.draw()
        meta.dataset._children = originalDatasets

        points.forEach(function(point) {
          point.draw(area)
        })
      },
    })
    Chart.defaults.multicolorLine = Chart.defaults.line
    Chart.controllers.multicolorScatter = Chart.controllers.line.extend({
      draw(ease) {
        var startIndex = 0,
          meta = this.getMeta(),
          points = meta.data || [],
          colors = this.getDataset().colors,
          area = this.chart.chartArea,
          originalDatasets = meta.dataset._children.filter(function(data) {
            return !isNaN(data._view.y)
          })

        function _setColor(newColor, meta) {
          meta.dataset._view.borderColor = newColor
        }

        if (!colors) {
          Chart.controllers.line.prototype.draw.call(this, ease)
          return
        }

        for (var i = 2; i <= colors.length; i++) {
          if (colors[i - 1] !== colors[i]) {
            _setColor(colors[i - 1], meta)
            meta.dataset._children = originalDatasets.slice(startIndex, i)
            meta.dataset.draw()
            startIndex = i - 1
          }
        }

        meta.dataset._children = originalDatasets.slice(startIndex)
        meta.dataset.draw()
        meta.dataset._children = originalDatasets

        points.forEach(function(point) {
          point.draw(area)
        })
      },
    })
  },

  mounted() {
    this.check_chart()
  },

  methods: {
    check_chart(reinit = false) {
      if (this.chart_object === undefined || reinit) {
        if (this.chart_object) {
          this.chart_object.real_chart.destroy()
        }
        this.chart_object = new PerformanceChart(
          this.$refs['performance-chart'],
          this.$t('performance'),
          this.y_min,
          this.y_max
        )
        this.chart_object.init()
        this.chart_datasets = []
      }
      for (const dataset of this.datasets) {
        const existing_dataset = _.find(this.chart_datasets, {
          label: dataset.label,
        })
        if (_.isUndefined(existing_dataset)) {
          this.chart_datasets.push(dataset)
        } else {
          existing_dataset.data = dataset.data
        }
      }
      const labels = _.keys(this.performance_data).sort()
      this.chart_object.update(this.chart_datasets, labels)
    },
    prepare_event(event_type, period_id, day, time, params = null) {
      let year, month
      ;[year, month] = parse_period_id(period_id)
      const month_index = month - 1
      let hours, minutes
      ;[hours, minutes] = parse_time(time)
      const date_o = new Date(
        Date.UTC(year, month_index, parseInt(day), hours, minutes)
      )
      const date = new Intl.DateTimeFormat(this.i18n_locale, {
        timeStyle: 'short',
        dateStyle: 'short',
      }).format(date_o)

      if (_.isNull(params)) {
        params = { period_id }
      }
      const event = this.$t(`${event_type}_event`, params)

      const color = this.$options.event_to_color[event_type]
      const _cellVariants = { date: 'light', event: color }

      const res = {
        date,
        event,
        _cellVariants,
      }
      return res
    },
    has_events(period_id) {
      const res = this.event_items_per_period_id[period_id].length != 0
      return res
    },
  },
}
</script>
